// Main layout with Drawer and Header
import React from 'react';
import { connect } from 'react-redux';
import { styled, Typography } from '@mui/material';
import { Tenant } from 'components/merchant/models/Tenant';
import { isString } from 'lib/typeguards';
import { ApplicationState } from 'store/store';
import { Column, ColumnProps } from 'ui/Flex';
import { MuiBreadcrumb, MuiBreadcrumbs } from 'ui/MuiBreadcrumbs';
import { useRemainingHeight } from 'lib/hooks/useRemainingHeight';

const StyledTypography = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const StyledHeaderRow = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        display: 'none'
    }
}));
const StyledFullHeightContainer = styled('div')({
    height: '100%',
    width: '100%'
});

interface MainLayoutProps extends React.ComponentProps<'div'> {
    pageName?: string;
    pageDescription?: string;
    headerActions?: JSX.Element | React.ReactNode;
    noBreadcrumbs?: boolean;
    tenant?: Tenant;
    breadcrumbs?: MuiBreadcrumb[];
    columnProps?: ColumnProps;
    noScroll?: boolean;
}

const mapStateToProps = ({ auth }: ApplicationState) => ({
    tenant: auth.tenant
});

export const MainLayout = connect(mapStateToProps)(
    ({
        children,
        pageName,
        pageDescription,
        tenant,
        columnProps,
        breadcrumbs,
        headerActions = null,
        noBreadcrumbs,
        noScroll
    }: MainLayoutProps) => {
        const childrenColumnProps = columnProps || { align: 'stretch' };
        const [height, ref] = useRemainingHeight();
        const contentStyle = React.useMemo(() => ({ height }), [height]);
        if (noScroll) {
            return (
                <StyledFullHeightContainer>
                    <StyledHeaderRow ref={ref}>
                        <Column>
                            <MuiBreadcrumbs
                                rootLabel={tenant && tenant.title}
                                items={breadcrumbs}
                                title={pageName}
                                naked={noBreadcrumbs}
                            />
                            {isString(pageDescription) && (
                                <StyledTypography gutterBottom variant="caption" color="textSecondary">
                                    {pageDescription}
                                </StyledTypography>
                            )}
                        </Column>
                        {headerActions}
                    </StyledHeaderRow>
                    <Column flex={1} {...childrenColumnProps} id="main-layout" style={contentStyle}>
                        {children}
                    </Column>
                </StyledFullHeightContainer>
            );
        }
        return (
            <Column>
                <StyledHeaderRow>
                    <Column>
                        <MuiBreadcrumbs
                            rootLabel={tenant && tenant.title}
                            items={breadcrumbs}
                            title={pageName}
                            naked={noBreadcrumbs}
                        />
                        {isString(pageDescription) && (
                            <StyledTypography gutterBottom variant="caption" color="textSecondary">
                                {pageDescription}
                            </StyledTypography>
                        )}
                    </Column>
                    {headerActions}
                </StyledHeaderRow>
                <Column flex={1} {...childrenColumnProps} id="main-layout">
                    {children}
                </Column>
            </Column>
        );
    }
);
